import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import {
  MERCHANT_ENTERPRISE_SERVICE,
  fetchBrandVenues,
  fetchBrands,
  fetchEnterprise,
  fetchVenues,
} from 'apis/enterprise/api';
import { BrandItem } from 'apis/enterprise/types';
import { BrandVenues } from 'modules/enterprise/types';
import { ExperienceEntity, ExperienceEntityTree, ExperienceEntityType } from 'modules/experience-core';

export const useEnterprise = (identifier: string, isEnterpriseFetchEnabled = true) => {
  const [brandsVenues, setBrandsVenues] = useState<BrandVenues[]>([]);
  const [isBrandVenuesLoading, setIsBrandVenuesLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    data: enterprise,
    isLoading: isLoadingEnterprise,
    isError,
  } = useQuery(
    [`/v1/enterprises/${identifier}`],
    () => {
      return identifier ? fetchEnterprise(identifier) : undefined;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isEnterpriseFetchEnabled,
    },
  );

  const { data: brands, isLoading: isLoadingBrands } = useQuery(
    [`${MERCHANT_ENTERPRISE_SERVICE}/enterprises/${identifier}/brands`],
    () => fetchBrands(identifier),
    {
      refetchOnMount: false,
      enabled: isEnterpriseFetchEnabled,
    },
  );

  useEffect(() => {
    async function fetchVenuesData(brands: BrandItem[]) {
      try {
        const venuesBrands = await Promise.all(
          brands.map(async item => {
            const venues = await fetchBrandVenues(item.brand.id);
            return {
              brand: item.brand,
              venuesIds: venues.map(item => item.venueId),
            };
          }),
        );

        const brandItemsWithVenues = venuesBrands.filter(item => item.venuesIds.length);

        const brandsVenues: BrandVenues[] = await Promise.all(
          brandItemsWithVenues.map(async brandItem => {
            const { venuesIds, brand } = brandItem;
            const { results: venues } = await fetchVenues(venuesIds);

            return {
              brand,
              venues,
            };
          }),
        );
        setBrandsVenues(brandsVenues);
        setIsBrandVenuesLoading(false);
      } catch (error) {
        setError(error);
      }
    }

    if (brands) {
      fetchVenuesData(brands);
    }
  }, [brands]);

  const isLoading = isLoadingEnterprise || isLoadingBrands || isBrandVenuesLoading;

  const experienceEntity: ExperienceEntity | null = useMemo(() => {
    if (!enterprise || isLoading) {
      return null;
    }
    return {
      experienceEntityId: enterprise.id,
      experienceEntityType: ExperienceEntityType.ENTERPRISE,
      experienceEntityObject: enterprise,
    };
  }, [isLoading, enterprise]);

  const experienceEntityTree: ExperienceEntityTree | null = useMemo(() => {
    if (isLoading || !experienceEntity || !brandsVenues) {
      return null;
    }
    return {
      experienceEntity,
      children: brandsVenues.map(brand => ({
        experienceEntity: {
          experienceEntityId: brand.brand.id,
          experienceEntityType: ExperienceEntityType.BRAND,
          experienceEntityObject: brand.brand,
        },
        children: brand.venues.map(venue => ({
          experienceEntity: {
            experienceEntityId: venue.id,
            experienceEntityType: ExperienceEntityType.VENUE,
            experienceEntityObject: venue,
          },
          children: [],
        })),
      })),
    };
  }, [isLoading, experienceEntity, brandsVenues]);

  return {
    experienceEntity,
    experienceEntityTree,
    error: isError && error,
    isLoading,
  };
};
